import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AgreementPopup from '../components/AgreementPopup'

const BUSINESS_TYPES = [
  { value: 'sole', label: 'Sole' },
  { value: 'proprietor', label: 'Proprietor' },
  { value: 'LLC/Ltd', label: 'LLC/Ltd' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'Independent freelance', label: 'Independent freelance' },
  { value: 'corporate', label: 'Corporate' }
]

const SUSTAINABILITY = ['Yes', 'No', 'Planning to']

const OnboardingPage = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState(null)
  const [showAgreement, setShowAgreement] = useState(false)
  const [form, setForm] = useState({
    businessType: '',
    businessAddress: '',
    websiteUrl: '',
    sustainableTravelSpecialization: '',
    certifications: []
  })

  useEffect(() => {
    const load = async () => {
      try {
        const res = await travelAgentService.getProfile()
        const data = res.data || res
        setProfile(data)
        travelAgentService.patchCachedUser(data)
        if (data.agreementAccepted) setStep((s) => Math.max(s, 2))
        setForm({
          businessType: data.businessType || '',
          businessAddress: data.businessAddress || '',
          websiteUrl: data.websiteUrl || '',
          sustainableTravelSpecialization: data.sustainableTravelSpecialization || '',
          certifications: data.certifications || []
        })
      } catch (e) {
        toast.error(e.message || 'Failed to load profile')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [navigate])

  const saveProfile = async () => {
    setSaving(true)
    try {
      const res = await travelAgentService.updateProfile(form)
      const data = res.data || res
      travelAgentService.patchCachedUser(data)
      setProfile((p) => ({ ...p, ...data }))
      toast.success('Profile saved')
      return true
    } catch (e) {
      toast.error(e.message || 'Save failed')
      return false
    } finally {
      setSaving(false)
    }
  }

  const finishOnboarding = async () => {
    const ok = await saveProfile()
    if (!ok) return
    const refreshed = await travelAgentService.getProfile()
    const data = refreshed.data || refreshed
    travelAgentService.patchCachedUser(data)
    toast.success('Profile saved')
    navigate('/dashboard')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Complete your Studio setup</h1>
        <p className="text-gray-600 text-sm mb-8">Step {step} of 3 — Partnership, business profile, and sustainability</p>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-gray-700">Review and accept the partnership agreement to continue.</p>
            <p className="text-sm text-gray-500">
              Status: {profile?.agreementAccepted ? 'Accepted' : 'Not yet accepted'}
            </p>
            <button
              type="button"
              onClick={() => setShowAgreement(true)}
              className="bg-slate-900 text-white px-6 py-2 rounded-lg"
            >
              {profile?.agreementAccepted ? 'View agreement' : 'Review agreement'}
            </button>
            {profile?.agreementAccepted && (
              <button type="button" onClick={() => setStep(2)} className="block mt-4 text-slate-900 underline">
                Continue to business information
              </button>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <label className="block text-sm font-medium">Business type</label>
            <select
              value={form.businessType}
              onChange={(e) => setForm({ ...form, businessType: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Select</option>
              {BUSINESS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <label className="block text-sm font-medium">Business address</label>
            <input
              value={form.businessAddress}
              onChange={(e) => setForm({ ...form, businessAddress: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            />
            <label className="block text-sm font-medium">Website (optional)</label>
            <input
              value={form.websiteUrl}
              onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="https://"
            />
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="text-gray-600">Back</button>
              <button type="button" onClick={() => setStep(3)} className="bg-slate-900 text-white px-6 py-2 rounded-lg">
                Next
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <label className="block text-sm font-medium">Sustainable travel specialization</label>
            <select
              value={form.sustainableTravelSpecialization}
              onChange={(e) => setForm({ ...form, sustainableTravelSpecialization: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Select</option>
              {SUSTAINABILITY.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setStep(2)} className="text-gray-600">Back</button>
              <button
                type="button"
                disabled={saving}
                onClick={finishOnboarding}
                className="bg-slate-900 text-white px-6 py-2 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Complete setup'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showAgreement && (
        <AgreementPopup
          isOpen={showAgreement}
          onClose={() => {
            setShowAgreement(false)
            travelAgentService.getProfile().then((res) => {
              const data = res.data || res
              setProfile(data)
              travelAgentService.patchCachedUser(data)
              if (data.agreementAccepted) setStep(2)
            })
          }}
          profile={profile}
        />
      )}
    </div>
  )
}

export default OnboardingPage
